import {getUtmCookiesMap} from "./UTM-tracking";
import {updateUserProperties} from "./amplitude";
import {getTestCookiesData} from "./ab-testing";
import {GDPRContextState} from "../contexts/GDPRContext";
import {ERROR_CAMPAIGN_ID, ERROR_INQUIRY_OBJECT} from "../constants/pardot";
import {logErrorToSentry} from "./sentry";
import {
  FormDataBase,
  MailingAddressObjectType,
  PhysicalAddressObjectType,
} from "../types";
import {getMsclkid} from "./microsoft-click-id";
import {getLastPaths} from "./path-logging";

const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY;

/**
 * utility for getting current datetime in in specific timezone in iso format
 * @param {string} tz
 * @return {string}
 */
export const getCurrentISOFormatDateTimeByTimezone = (
  tz: string = "America/Los_Angeles"
) => {
  const baseDt = new Date();
  const dt = new Date(baseDt.toLocaleString("en-US", {timeZone: tz}));

  const padValue = (val: number) => {
    return val.toString().padStart(2, "0");
  };

  const utcDate = new Date(baseDt.toLocaleString("en-US", {timeZone: "UTC"}));

  const offsetMins = (utcDate.getTime() - dt.getTime()) / 6e4;

  const offsetHours = Math.ceil(offsetMins / 60);
  const offsetBalance = offsetMins - offsetHours * 60;

  const offset = `${offsetMins < 0 ? "-" : "+"}${padValue(
    Math.abs(offsetHours)
  )}:${padValue(Math.abs(offsetBalance))}`;

  return `${dt.getFullYear()}-${padValue(dt.getMonth() + 1)}-${padValue(
    dt.getDate()
  )}T${padValue(dt.getHours())}:${padValue(dt.getMinutes())}:${padValue(
    dt.getSeconds()
  )}${offset}`;
};

/**
 * utility for getting current date in in specific timezone in iso format
 * @param {string} tz
 * @return {string}
 */
export const getCurrentISOFormatDateByTimezone = (
  tz: string = "America/Los_Angeles"
) => {
  const baseDt = new Date();
  const dt = new Date(baseDt.toLocaleString("en-US", {timeZone: tz}));

  const padValue = (val: number) => {
    return val.toString().padStart(2, "0");
  };

  return `${dt.getFullYear()}-${padValue(dt.getMonth() + 1)}-${padValue(
    dt.getDate()
  )}`;
};

/**
 *
 * @return {Promise<any>}
 */
export const getRecaptchaToken = (): Promise<any> => {
  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    try {
      window.grecaptcha.enterprise.ready(() => {
        try {
          window.grecaptcha.enterprise
            .execute(RECAPTCHA_SITE_KEY, {action: "submit"})
            .then((token: any) => {
              return resolve(token);
            })
            .catch((error: any) => {
              return reject(error);
            });
        } catch (err) {
          return reject(err);
        }
      });
    } catch (error) {
      return reject(error);
    }
  });
};

const EXCLUDED_FORMDATA_PROPS: Array<string> = [
  "privacyPolicyAccepted",
  "lastInquiryObject",
  "campaignId",
];

/**
 * helper method for form submission to pardot
 * @param {FormDataBase | any} data
 * @param {GDPRContextState} gdpr
 * @return {Promise<any>}
 */
export const submitFormData = (
  data: FormDataBase | any,
  gdpr: GDPRContextState
): Promise<any> => {
  const optInMarketingEmails = !!data.Express_Opt_In || gdpr.isEU;
  const utmMap = getUtmCookiesMap();
  const abTestData = getTestCookiesData();
  const currentTime = getCurrentISOFormatDateByTimezone();
  const userJourneyPaths = ["", "", ...getLastPaths(2)].slice(-2);

  const {campaignId, lastInquiryObject} = data;

  // cleaned form data should exclude the EXCLUDED_FORMDATA_PROPS props
  const cleanedFormData = {...data};

  EXCLUDED_FORMDATA_PROPS.forEach(item => {
    delete cleanedFormData[item];
  });

  const privacyPolicyAccepted =
    data.Express_Opt_In && data.Express_Opt_In.length > 0 ? true : false;

  /* eslint-disable camelcase */
  const formData = {
    ...cleanedFormData,
    campaign_id: campaignId != null ? campaignId : ERROR_CAMPAIGN_ID,
    ...(privacyPolicyAccepted && {Express_Opt_In_Date: currentTime}),
    Express_Opt_In: privacyPolicyAccepted,
    ...(optInMarketingEmails && {Express_Promotional_Opt_In_Date: currentTime}),
    Express_Promotional_Opt_In: optInMarketingEmails,
    Flexport_User_Journey_Page_1: userJourneyPaths[0],
    Flexport_User_Journey_Page_2: userJourneyPaths[1],
    gclid: utmMap.gclid,
    GSC_Modal: utmMap.GSC_Modal,
    IP_Inferred_Country_Code: gdpr.countryCode,
    Last_inquiry_date: currentTime,
    Last_inquiry_object:
      lastInquiryObject != null ? lastInquiryObject : ERROR_INQUIRY_OBJECT,
    msclkid: getMsclkid(),
    Original_Referrer: utmMap.referrer,
    split_test: abTestData,
    utm_adgroup: utmMap.utm_adgroup,
    utm_asset: utmMap.utm_asset,
    utm_campaign: utmMap.utm_campaign,
    utm_content: utmMap.utm_content,
    utm_device: utmMap.utm_device,
    utm_matchtype: utmMap.utm_matchtype,
    utm_medium: utmMap.utm_medium,
    utm_network: utmMap.utm_network,
    utm_source: utmMap.utm_source,
    utm_term: utmMap.utm_term,
  };

  updateUserProperties({});

  return new Promise(async (resolve, reject) => {
    try {
      const grecaptchaToken = await getRecaptchaToken();
      const response = await fetch(
        "/.netlify/functions/pardot-prospects-background-v2",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${grecaptchaToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      return response.ok === true ? resolve(response) : reject(response);
    } catch (err) {
      return reject(err);
    }
  });
};

export const resetMailingObject = {
  address_one: "",
  address_two: "",
  city: "",
  country: "",
  state: "",
  zip: "",
  mailingAddressSet: false,
};

export const resetPhysicalObject = {
  physical_city: "",
  physical_postal_code: "",
  physical_state: "",
  physical_street_address: "",
  physical_street_address2: "",
  physical_country: "",
  physicalAddressSet: false,
};

/**
 *
 * @param {any} addressComponents
 * @param {any} addressTypeName
 * @param {boolean} shortName
 * @param {boolean} isStreetNumber
 * @return {any}
 */
const _getAddressFieldData = (
  addressComponents: any,
  addressTypeName: any,
  shortName = false,
  isStreetNumber = false
) => {
  if (isStreetNumber === true) {
    const res = addressComponents.find((a: any) =>
      a?.types?.includes(addressTypeName)
    )?.long_name;
    return res != null ? `${res} ` : "";
  }
  if (shortName === true)
    return (
      addressComponents.find((a: any) => a?.types?.includes(addressTypeName))
        ?.short_name || ""
    );
  return (
    addressComponents.find((a: any) => a?.types?.includes(addressTypeName))
      ?.long_name || ""
  );
};

/**
 *
 * @param {string} placeId
 * @param {string} sessionToken
 * @param {string} addressType
 * @param {boolean} sameAsMailing
 * @return {Promise<MailingAddressObjectType | PhysicalAddressObjectType | any>}
 */
export const getFullAddressDetails = async (
  placeId: string,
  sessionToken: string,
  addressType: "mailing" | "physical",
  sameAsMailing: boolean
): Promise<MailingAddressObjectType | PhysicalAddressObjectType | any> => {
  const addressObject =
    addressType === "mailing" ? resetMailingObject : resetPhysicalObject;
  try {
    const data = await fetch("/.netlify/functions/places-details-api", {
      body: JSON.stringify({
        placeId,
        sessionToken,
      }),
      method: "POST",
    });

    const response = await data.json();
    let newAddressObject;
    if (response.result) {
      const {address_components} = response.result;
      if (addressType === "mailing") {
        newAddressObject = {
          address_one: `${_getAddressFieldData(
            address_components,
            "street_number",
            false,
            true
          )}${_getAddressFieldData(address_components, "route", true)}`,
          address_two: _getAddressFieldData(address_components, "subpremise"),
          city: _getAddressFieldData(address_components, "locality"),
          country: _getAddressFieldData(address_components, "country"),
          state: _getAddressFieldData(
            address_components,
            "administrative_area_level_1"
          ),
          zip: _getAddressFieldData(address_components, "postal_code"),
          mailingAddressSet: true,
        };
        if (sameAsMailing === true)
          newAddressObject = {
            physical_street_address: newAddressObject.address_one,
            physical_street_address2: newAddressObject.address_two,
            physical_city: newAddressObject.city,
            physical_country: newAddressObject.country,
            physical_state: newAddressObject.state,
            physical_postal_code: newAddressObject.zip,
            physicalAddressSet: true,
            ...newAddressObject,
          };
      } else {
        newAddressObject = {
          physical_street_address: `${_getAddressFieldData(
            address_components,
            "street_number"
          )} ${_getAddressFieldData(address_components, "route", true)}`,
          physical_street_address2: _getAddressFieldData(
            address_components,
            "subpremise"
          ),
          physical_city: _getAddressFieldData(address_components, "locality"),
          physical_country: _getAddressFieldData(address_components, "country"),
          physical_state: _getAddressFieldData(
            address_components,
            "administrative_area_level_1"
          ),
          physical_postal_code: _getAddressFieldData(
            address_components,
            "postal_code"
          ),
          physicalAddressSet: true,
        };
      }
      return newAddressObject;
    }
  } catch (error: any) {
    logErrorToSentry(error);
  }
  return addressObject;
};

export const injectFieldsIntoForm = (
  data: any,
  $form: HTMLFormElement
): void => {
  Object.keys(data).forEach(key => {
    const matchedElements = [...$form.elements].filter(element => {
      return (element as HTMLInputElement).name === key;
    });

    if (matchedElements.length) {
      matchedElements.forEach(matched => {
        (matched as HTMLInputElement).value = data[key];
      });
    } else {
      // Otherwise, create a new hidden input element and append it to the form
      const newInput = document.createElement("input");
      newInput.type = "hidden";
      newInput.name = key;
      newInput.value = data[key];

      // Append the new input to the form
      $form.appendChild(newInput);
    }
  });
};
